import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { loginSuccess } from "../store/actions/userActions";
import { loginApi } from "../store/api/authApi";

import logoImage from "../image/logoadmin.png";

import BrandButton from "../ChildComponents/BrandButton";
import Input from "../ChildComponents/Input";

function LoginAndRegistration() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(" ");
  const [loading, setLoading] = useState(false);

  const mutation = useMutation({
    mutationFn: (data) => {
      return loginApi(data);
    },
    onSuccess: (data) => {
      dispatch(loginSuccess({ token: data.token }));
      setLoading(false);
      setError(data.message);
      reset();
      navigate("/dashboard", { replace: true });
    },
    onError: (error) => {
      setError(error.message || "An unknown error occurred");
      setTimeout(() => setError(""), 8000);
      setLoading(false);
    },
  });

  const handleSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setLoading(true);
    setError("");
    try {
      mutation.mutate({
        email,
        password,
      });
    } catch (error) {
      console.error("Caught error in handleSubmit:", error.message);
      setError(error.message);
      setTimeout(() => setError(" "), 8000);
    }
  };

  const reset = () => {
    setEmail("");
    setPassword("");
  };

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light position-relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="position-absolute w-100 h-100" style={{ zIndex: 0 }}>
        <div
          className="position-absolute rounded-circle bg-primary opacity-10"
          style={{
            width: "500px",
            height: "500px",
            top: "-200px",
            right: "-100px",
            animation: "float 6s ease-in-out infinite",
          }}
        />
        <div
          className="position-absolute rounded-circle bg-info opacity-10"
          style={{
            width: "400px",
            height: "400px",
            bottom: "-150px",
            left: "-100px",
            animation: "float 8s ease-in-out infinite reverse",
          }}
        />
      </div>

      <style>
        {`
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
          }
          
          .login-card {
            backdrop-filter: blur(10px);
            background: rgba(255, 255, 255, 0.95);
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
            transition: transform 0.3s ease, box-shadow 0.3s ease;
          }
          
          .login-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 12px 48px rgba(0, 0, 0, 0.15);
          }
          
          .brand-title {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            font-weight: 800;
            letter-spacing: -0.5px;
          }
          
          .subtitle {
            color: #6c757d;
            font-size: 0.95rem;
            font-weight: 500;
          }
          
          .error-alert {
            animation: slideIn 0.3s ease-out;
          }
          
          @keyframes slideIn {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>

      <div className="container position-relative" style={{ zIndex: 1 }}>
        <div className="row justify-content-center">
          <div className="col-12 col-sm-10 col-md-8 col-lg-5 col-xl-4">
            <div className="card login-card border-0 rounded-4 p-4">
              <div className="card-body">
                {/* Header */}
                <div className="text-center mb-4">
                  <div className="mb-3">
                    <img src={logoImage} className="img-fluid" alt="" />
                  </div>
                  <h1
                    className="brand-title mb-2"
                    style={{ fontSize: "1.75rem" }}
                  >
                    News Paper ADMIN
                  </h1>
                  <p className="subtitle mb-0">
                    Sign in to manage your content
                  </p>
                </div>

                {/* Form */}
                <div>
                  <div className="mb-3">
                    <Input
                      icon="alternate_email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      type="email"
                      placeholder="Email"
                    />
                  </div>

                  <div className="mb-3">
                    <Input
                      icon="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      type="password"
                      placeholder="Password"
                    />
                  </div>

                  {/* Error Message */}
                  {error && error.trim() !== "" && (
                    <div
                      className="alert alert-danger error-alert py-2 px-3 mb-3"
                      role="alert"
                    >
                      <div className="d-flex align-items-center">
                        <span
                          className="material-icons me-2"
                          style={{ fontSize: "18px" }}
                        >
                          error_outline
                        </span>
                        <small>{error}</small>
                      </div>
                    </div>
                  )}

                  {/* Submit Button */}
                  <div onClick={handleSubmit}>
                    <BrandButton
                      title={!loading ? "Login" : "Loading...."}
                      className="w-100"
                      disabled={loading}
                    />
                  </div>

                  {/* Footer Links */}
                  <div className="text-center mt-4">
                    <small className="text-muted">
                      Protected by secure authentication
                    </small>
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Info */}
            <div className="text-center mt-3">
              <small className="text-muted">
                <span
                  className="material-icons align-middle me-1"
                  style={{ fontSize: "16px" }}
                >
                  lock
                </span>
                Secure Admin Portal
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginAndRegistration;
